/** All my groups, my balance in each, and a way to start a new one. */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Spinner } from "../../components/ui/spinner";
import { useGroups } from "../../components/layout/GroupSwitcher";
import { ThemeToggle } from "../../components/layout/ThemeToggle";
import { api, ApiError, type Group } from "../../lib/api";
import { formatSigned, toneClass } from "../../lib/money";
import { cn } from "../../lib/utils";
import { PushBanner } from "../auth/PushSettings";
import { useSession } from "../auth/session";

export function DashboardPage() {
  const { user, logout } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const groups = useGroups();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.post<Group>("/groups", { name: name.trim(), base_currency: "USD" }),
    onSuccess: (group) => {
      void queryClient.invalidateQueries({ queryKey: ["groups"] });
      navigate(`/g/${group.id}`);
    },
    onError: (cause) =>
      setError(cause instanceof ApiError ? cause.message : "Could not create the group."),
  });

  return (
    <div className="scroll-region h-[100dvh] overflow-y-auto">
      <main className="mx-auto w-full max-w-2xl px-5 py-8 sm:px-6">
        <PushBanner />
        {/*
          One line on a phone. "Signed in as …" was the only thing pushing the account
          controls onto a second row, and it says nothing you cannot see on the Settings
          page — the name now lives on the Sign out button's tooltip instead.
        */}
        <header className="mb-8 flex items-center justify-between gap-3">
          <h1 className="display min-w-0 truncate text-lg">The Sheet</h1>
          <nav className="flex shrink-0 items-center gap-0.5" aria-label="Account">
            <ThemeToggle />
            <Link
              to="/settings"
              className="rounded-lg px-2.5 py-2 text-xs text-muted transition-colors hover:bg-ink/[0.04] hover:text-ink"
            >
              Settings
            </Link>
            <button
              type="button"
              className="rounded-lg px-2.5 py-2 text-xs text-muted transition-colors hover:bg-ink/[0.04] hover:text-ink"
              onClick={() => void logout().then(() => navigate("/login"))}
              title={user?.name ? `Signed in as ${user.name}` : undefined}
            >
              Sign out
            </button>
          </nav>
        </header>

        {groups.isPending ? (
          <Spinner />
        ) : (
          <ul className="flex flex-col gap-2">
            {(groups.data ?? []).map((group) => (
              <li key={group.id}>
                <Link
                  to={`/g/${group.id}`}
                  className={cn(
                    "flex items-center justify-between gap-6 rounded-xl border border-rule",
                    "bg-raised px-4 py-4 transition-all duration-150",
                    "hover:-translate-y-px hover:border-ink/20 hover:shadow-sm",
                  )}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{group.name}</span>
                    <span className="mt-0.5 block text-xs text-muted">
                      {group.member_count} {group.member_count === 1 ? "member" : "members"}
                      {group.archived_at ? " · archived" : ""}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className={cn("num text-lg", toneClass(group.my_balance_base_cents))}>
                      {formatSigned(group.my_balance_base_cents, group.base_currency)}
                    </span>
                    <span className="mt-0.5 block text-2xs uppercase tracking-wide text-muted">
                      {group.my_balance_base_cents > 0
                        ? "you are owed"
                        : group.my_balance_base_cents < 0
                          ? "you owe"
                          : "settled"}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
            {(groups.data ?? []).length === 0 ? (
              <li className="rounded-xl border border-dashed border-rule py-10 text-center text-sm text-muted">
                No groups yet. Create one to get started.
              </li>
            ) : null}
          </ul>
        )}

        <div className="mt-6">
          {error ? (
            <p role="alert" className="mb-2 text-sm text-owing">
              {error}
            </p>
          ) : null}
          {creating ? (
            <form
              className="flex gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                setError(null);
                if (name.trim()) create.mutate();
              }}
            >
              <Input
                autoFocus
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Group name (House, Japan trip…)"
                aria-label="Group name"
              />
              <Button type="submit" variant="primary" disabled={create.isPending}>
                Create
              </Button>
              <Button type="button" onClick={() => setCreating(false)}>
                Cancel
              </Button>
            </form>
          ) : (
            <Button variant="primary" onClick={() => setCreating(true)}>
              + New group
            </Button>
          )}
        </div>

        {user?.is_superadmin ? (
          <p className="mt-10 text-xs text-muted">
            <Link to="/admin" className="text-accent underline underline-offset-2">
              Superadmin console
            </Link>
          </p>
        ) : null}
      </main>
    </div>
  );
}
